import React from "react";
import Icon from "../../../components/AppIcon";

const DIFFICULTY_LEVELS = [
  { value: "all", label: "All Levels", icon: "Layers", color: "text-gray-500" },
  { value: "1-2", label: "Easy", icon: "TrendingUp", color: "text-green-600" },
  { value: "3", label: "Moderate", icon: "Mountain", color: "text-blue-600" },
  { value: "4", label: "Challenging", icon: "Zap", color: "text-orange-500" },
  { value: "5", label: "Expert", icon: "Crown", color: "text-red-600" },
];

const SEASON_OPTIONS = [
  { value: "all", label: "All Seasons", icon: "Calendar" },
  { value: "spring", label: "Spring (Mar–May)", icon: "Flower2" },
  { value: "autumn", label: "Autumn (Sep–Nov)", icon: "Leaf" },
  { value: "winter", label: "Winter (Dec–Feb)", icon: "Snowflake" },
];

const DURATION_OPTIONS = [
  { value: "all", label: "Any Duration" },
  { value: "3-7", label: "3–7 days" },
  { value: "8-14", label: "8–14 days" },
  { value: "15-21", label: "15–21 days" },
  { value: "22+", label: "22+ days" },
];

const ELEVATION_OPTIONS = [
  { value: "all", label: "Any Elevation" },
  { value: "3000", label: "Under 3,000m" },
  { value: "4000", label: "Under 4,000m" },
  { value: "5000", label: "Under 5,000m" },
  { value: "5000+", label: "Above 5,000m" },
];

const FilterSidebar = ({ filters, onFilterChange, onClearFilters, isOpen, onToggle, mobileOnly = false }) => {
  const hasActiveFilters = Object.entries(filters).some(
    ([, v]) => v !== "all" && v !== ""
  );

  const Section = ({ title, icon: sectIcon, children }) => (
    <div className="mb-7">
      <div className="flex items-center gap-2 mb-3">
        {sectIcon && <Icon name={sectIcon} size={14} className="text-primary/60" />}
        <p className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
          {title}
        </p>
      </div>
      {children}
    </div>
  );

  const Chip = ({ value, current, label, icon, color, onClick }) => {
    const active = current === value;
    return (
      <button
        onClick={() => onClick(value)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 ${
          active
            ? "bg-primary text-white border-primary shadow-sm"
            : "bg-white text-gray-700 border-border hover:border-primary/50 hover:text-primary hover:bg-primary/5"
        }`}
      >
        {icon && (
          <Icon
            name={icon}
            size={13}
            className={active ? "text-white" : color || "text-gray-400"}
          />
        )}
        {label}
      </button>
    );
  };

  const content = (
    <div className="p-5 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="SlidersHorizontal" size={15} className="text-primary" />
          </div>
          <h3 className="font-headline font-semibold text-base text-foreground">Filters</h3>
          {hasActiveFilters && (
            <span className="w-2 h-2 rounded-full bg-accent inline-block" />
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="text-xs text-accent hover:underline font-medium"
            >
              Clear all
            </button>
          )}
          <button onClick={onToggle} className="lg:hidden p-1 rounded hover:bg-muted">
            <Icon name="X" size={18} className="text-text-secondary" />
          </button>
        </div>
      </div>

      <Section title="Difficulty" icon="Activity">
        <div className="flex flex-wrap gap-2">
          {DIFFICULTY_LEVELS.map((d) => (
            <Chip
              key={d.value}
              value={d.value}
              current={filters.difficulty}
              label={d.label}
              icon={d.icon}
              color={d.color}
              onClick={(v) => onFilterChange("difficulty", v)}
            />
          ))}
        </div>
      </Section>

      <Section title="Best Season" icon="Calendar">
        <div className="flex flex-wrap gap-2">
          {SEASON_OPTIONS.map((s) => (
            <Chip
              key={s.value}
              value={s.value}
              current={filters.season}
              label={s.label}
              icon={s.icon}
              onClick={(v) => onFilterChange("season", v)}
            />
          ))}
        </div>
      </Section>

      <Section title="Duration" icon="Clock">
        <div className="flex flex-wrap gap-2">
          {DURATION_OPTIONS.map((d) => (
            <Chip
              key={d.value}
              value={d.value}
              current={filters.duration}
              label={d.label}
              onClick={(v) => onFilterChange("duration", v)}
            />
          ))}
        </div>
      </Section>

      <Section title="Max Elevation" icon="TrendingUp">
        <div className="space-y-1.5">
          {ELEVATION_OPTIONS.map((e) => {
            const active = filters.elevation === e.value;
            return (
              <button
                key={e.value}
                onClick={() => onFilterChange("elevation", e.value)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm border transition-all duration-150 ${
                  active
                    ? "bg-primary/10 border-primary text-primary font-medium"
                    : "bg-white border-border text-gray-700 hover:border-primary/40 hover:bg-primary/5"
                }`}
              >
                <span>{e.label}</span>
                <Icon
                  name="Mountain"
                  size={14}
                  className={active ? "text-primary" : "text-gray-300"}
                />
              </button>
            );
          })}
        </div>
      </Section>
    </div>
  );

  return (
    <>
      {!mobileOnly && (
        <div className="lg:hidden fixed bottom-6 left-4 z-30">
          <button
            onClick={onToggle}
            className="flex items-center gap-2 bg-accent text-white px-4 py-2.5 rounded-full shadow-lg text-sm font-medium hover:bg-accent/90 transition-colors"
          >
            <Icon name="SlidersHorizontal" size={16} />
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-white inline-block" />
            )}
          </button>
        </div>
      )}

      {mobileOnly ? (
        <>
          <div
            className={`fixed top-0 left-0 h-full w-72 bg-white z-30 shadow-xl transform transition-transform duration-300 ${
              isOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            {content}
          </div>
          {isOpen && (
            <div className="fixed inset-0 bg-black/40 z-20" onClick={onToggle} />
          )}
        </>
      ) : (
        <div className="bg-white rounded-2xl border border-border/50 shadow-sm">
          {content}
        </div>
      )}
    </>
  );
};

export default FilterSidebar;
